import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import ContentOverlay from './ContentOverlay';
import Module from './Module';

import allocate from '@allocate/config';
import finsight from '@finsight/config';
import dataroomConfig from '@dataroom/config';
import tenantEvercall from '@evercall/config';
import dealroadshow from '@dealroadshow/config';

import SlickSlider from '@/Framework/UI/Molecules/SlickSlider';
import { ScheduleDemoModalLegacy } from '@/Framework/UI/Organisms/ScheduleDemo';
import {
  DEALVDR_PRICING_LINK,
  LEARN_MORE_DEALROADSHOW,
  LEARN_MORE_DEALVDR,
  LEARN_MORE_EVERCALL,
  LEARN_MORE_FINSIGHT,
  LEARN_MORE_INVESTOR_SET,
  LEARN_MORE_VERISEND,
  LEARN_MORE_17G5,
  LEARN_MORE_ALLOCATE,
  EXPLORE_CAPEDGE,
  slickConfigResponsive,
  getImagesByTenant,
  slickModuleKeys,
} from './constants';
import { isScreenS } from '@dealroadshow/uikit/core/styles/screen/screen';
import getSlickModuleConfig from './slickModuleConfig';
import FinsightBookDemo from '@/finsight/ui/components/staticPages/common/FinsightBookDemo';
import RequestServiceModal from '@/finsight/ui/common/components/RequestServiceModal';
import RequestServiceModalProvider, {
  useRequestServiceModalContext,
} from '@/finsight/application/RequestServiceModalContext';
import { ServiceType } from '@/finsight/domain/ServiceType';

import finsightIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/analytics.inline.svg';
import allocateIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/allocate.inline.svg';
import investorSetIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/investorSet.inline.svg';
import veriSendIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/veriSend.inline.svg';
import dealroadshowIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/dealroadshow.inline.svg';
import dealvdrIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/dealvdr.inline.svg';
import Icon17g5 from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/icon17g5.inline.svg';
import evercallIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/phone.inline.svg';
import capedgeIcon from '@/finsight/ui/components/staticPages/Company/OurSolutions/assets/icons/capedge.inline.svg';

import styles from './ourSolutions.scss';

const {
  tenantDealvdr,
  tenant17g5,
  tenantInvestorSet,
  tenantVeriSend,
} = dataroomConfig.tenant;

const OurSolutions = () => {
  const [scheduleDemoConfig, setScheduleDemoConfig] = useState(null);
  const [showIcon, setShowIcon] = useState(!isScreenS());
  const { openModal } = useRequestServiceModalContext();

  useEffect(() => {
    const resizeHandler = () => setShowIcon(!isScreenS());
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  const onModalClose = useCallback(() => {
    setScheduleDemoConfig(null);
  }, [
    setScheduleDemoConfig,
  ]);

  const slickModuleConfig = getSlickModuleConfig({
    [slickModuleKeys.API]: () => openModal(ServiceType.API),
    [slickModuleKeys.DRS_LIVE_VIDEO]: () => setScheduleDemoConfig({
      tenant: dealroadshow.code,
      tenantName: dealroadshow.name,
    }),
  });

  return (
    <div className={ cn(styles.contentWrapper) }>
      <section className={ styles.textContainer }>
        <div className={ styles.title }>Your time is valuable, we can help.</div>
        <p>
          FINSIGHT offers a suite of fully integrated, best-in-class applications that streamline your workflow,
          reduce costs and provide unparalleled visibility into your transaction process.
          All are backed by our Fanatical 24x7 Support.
          Take them for a spin or schedule a demo
        </p>
      </section>
      <ContentOverlay
        images={ getImagesByTenant(allocate) }
        icon={ allocateIcon }
        showIcon={ showIcon }
        textPosition="left"
        title="Allocate"
        contentText={ (
          <p>
            A powerful archive and visualization solution for final allocations of a new issue book building process.
            It’s secure, intuitive, yields incredible insights and is fully integrated into our leading Deal Roadshow
            platform.
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            name: 'Book Demo Allocate',
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: allocate.code,
              tenantName: allocate.name,
            }),
          },
          buttonConfig: {
            name: 'Learn more Allocate',
            content: 'Learn more',
            link: LEARN_MORE_ALLOCATE,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant({ code: 'capedge' }) }
        icon={ capedgeIcon }
        showIcon={ showIcon }
        textPosition="right"
        title="CapEdge"
        contentText={ (
          <p>
            Instantly search millions of SEC (EDGAR) filings and earnings transcripts and view insider and
            institutional investor ownership with over 20 years of historical data. Access critical insights,
            thoughtfully organized, with real-time email alerts. 100% free.
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            name: 'Explore CapEdge',
            content: 'Explore',
            link: EXPLORE_CAPEDGE,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(dealroadshow) }
        icon={ dealroadshowIcon }
        showIcon={ showIcon }
        textPosition="left"
        title="Deal Roadshow"
        contentText={ (
          <p>
            Industry-leading digital roadshow platform used to market deal and non-deal roadshows for equity, debt,
            municipal and private placement offerings.
            Great investor experience; streamlined underwriter workflows; unparalleled insight into the syndication
            process.
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            name: 'Book Demo DealRoadshow',
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: dealroadshow.code,
              tenantName: dealroadshow.name,
            }),
          },
          buttonConfig: {
            name: 'Learn more DealRoadshow',
            content: 'Learn more',
            link: LEARN_MORE_DEALROADSHOW,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(tenantDealvdr) }
        icon={ dealvdrIcon }
        showIcon={ showIcon }
        textPosition="right"
        title="DealVDR"
        contentText={ (
          <p>
            <span>
              An intuitive, lightning fast, secure virtual data room.{ ' ' }
              DealVDR offers robust permissioning, drag & drop uploading,{ ' ' }
              unlimited users & pages, and 24x7 fanatical support.
              Flexible 90-day (free) setup period and a{ ' ' }
              <a className={ styles.link } href={ DEALVDR_PRICING_LINK }>
                20% savings guarantee
              </a> * over comparable data room providers.
            </span>
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            name: 'Book Demo DealVDR',
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: tenantDealvdr.code,
              tenantName: tenantDealvdr.name,
            }),
          },
          buttonConfig: {
            name: 'Learn more DealVDR',
            content: 'Learn more',
            link: LEARN_MORE_DEALVDR,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(tenantEvercall) }
        icon={ evercallIcon }
        showIcon={ showIcon }
        textPosition="left"
        title="Evercall"
        contentText={ (
          <p>
            Evercall is a secure, reliable and highly professional operator assisted conference call service that
            specializes in capital markets and corporate finance events.
            It’s backed by fanatical 24x7 support and available globally.
            We’re ready when you are, schedule your call today.
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: tenantEvercall.code,
              tenantName: tenantEvercall.name,
            }),
          },
          buttonConfig: {
            content: 'Learn more',
            link: LEARN_MORE_EVERCALL,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(finsight) }
        icon={ finsightIcon }
        showIcon={ showIcon }
        textPosition="right"
        title="Finsight.com"
        contentText={ (
          <p>
            New issue deal database and search engine for global fixed income securities across ABS, IG and HY
            corporates.
            Browse by region, industry, sector, sponsor, and issuer.
            Highly accessible (mobile friendly) and intuitively organized.
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            component: FinsightBookDemo,
          },
          buttonConfig: {
            name: 'Learn more Finsight',
            content: 'Learn more',
            link: LEARN_MORE_FINSIGHT,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(tenantInvestorSet) }
        icon={ investorSetIcon }
        showIcon={ showIcon }
        textPosition="left"
        title="InvestorSet"
        contentText={ (
          <p>
            InvestorSet is a document library for corporates to streamline investor access
            to deal documentation and periodic reporting - improving liquidity in secondary markets.
            It’s secure, cost effective and backed by fanatical 24x7 support.
          </p>
) }
        buttons={ {
          blueButtonConfig: {
            name: 'Book Demo InvestorSet',
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: tenantInvestorSet.code,
              tenantName: tenantInvestorSet.name,
            }),
          },
          buttonConfig: {
            name: 'Learn more InvestorSet',
            content: 'Learn more',
            link: LEARN_MORE_INVESTOR_SET,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(tenantVeriSend) }
        icon={ veriSendIcon }
        showIcon={ showIcon }
        title="VeriSend"
        textPosition="right"
        contentText={ (
          <>
            <p>VeriSend is a secure electronic document delivery service for capital markets. It’s secure, cost
              effective and backed by fanatical 24x7 support.
            </p>
            <p>VeriSend is free for qualified investors.</p>
          </>
        ) }
        buttons={ {
          blueButtonConfig: {
            name: 'Book Demo VeriSend',
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: tenantVeriSend.code,
              tenantName: tenantVeriSend.name,
            }),
          },
          buttonConfig: {
            name: 'Learn more VeriSend',
            content: 'Learn more',
            link: LEARN_MORE_VERISEND,
          },
        } }
      />
      <ContentOverlay
        images={ getImagesByTenant(tenant17g5) }
        icon={ Icon17g5 }
        showIcon={ showIcon }
        title="17g5.com"
        textPosition="left"
        contentText={ (
          <p>
            The leading data room for SEC Rule 17g-5 compliance. It’s cost effective and easy to manage with email and
            audio uploading.
            Using another provider? We guarantee thousands of dollars in savings and offer fast migration services free
            of charge.
          </p>
        ) }
        buttons={ {
          blueButtonConfig: {
            name: 'Book Demo 17g5',
            content: 'Book Demo',
            onClick: () => setScheduleDemoConfig({
              tenant: tenant17g5.code,
              tenantName: tenant17g5.name,
            }),
          },
          buttonConfig: {
            name: 'Learn more 17g5',
            content: 'Learn more',
            link: LEARN_MORE_17G5,
          },
        } }
      />
      <section className={ styles.container }>
        <div className={ styles.textContainer }>
          <div className={ styles.title }>Additional Modules</div>
          <p>
            These modules are additional stand-alone micro-services embedded into our applications.
          </p>
        </div>
        <div className={ styles.modulesContainer }>
          <div className={ styles.modulesOverlay }>
            <div className={ styles.modulesSlider }>
              <SlickSlider
                // @ts-ignore
                className={ styles.moduleSlick }
                { ...slickConfigResponsive }
              >
                { slickModuleConfig.map((item) => <Module { ...item } />) }
              </SlickSlider>
            </div>
          </div>
        </div>
      </section>
      { scheduleDemoConfig && (
        <ScheduleDemoModalLegacy
          tenant={ scheduleDemoConfig.tenant }
          tenantName={ scheduleDemoConfig.tenantName }
          afterClose={ onModalClose }
        />
      ) }
      <RequestServiceModal />
    </div>
  );
};

export default () => (
  <RequestServiceModalProvider>
    <OurSolutions />
  </RequestServiceModalProvider>
);
